<script setup lang="ts">
import { useVehicleApi } from '@app/MyGarage';
import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import type { IErrorCodeDTO } from '@domain/CarePlan/HealthMonitor/IErrorCode';
import { useBackButton } from '@app/Components/Utilities';
import { Loading } from "@app/Components/Layout/Loading";

const scanEventId = useRoute().params.scanEventId as string;
const errorCodes = ref<IErrorCodeDTO[]>( [] );
const loading = ref<boolean>( true );

useBackButton();

onMounted(() => 
{
    fetchErrorDetails();
});

/**
 * Fetches error details for the current scan event and updates the errorCodes ref.
 * Sets the loading state to false once the fetch is complete which allows the page to render.
 */
const fetchErrorDetails = async () =>
{
    try
    {
        const result = await useVehicleApi().getErrorsForScanEvent( scanEventId );
        errorCodes.value = result.errors;
    }
    catch( error )
    {
        console.error( 'Failed to fetch error details:', error );
    }
    finally
    {
        loading.value = false;
    }
};

/* Helper function used to categorize the error codes by assessing the first character of the code
 * @param query - The query to check against
 * @returns true if any error code starts with the query, false otherwise
 */
const anyErrorCodeStartsWith = ( query: string ) => 
{
    return errorCodes.value.some( errorCode => errorCode.errorCode.startsWith( query ) );
};

</script>

<template>
    <div v-if="loading" class="flex h-full w-full justify-center items-center align-center">
        <Loading />
    </div>

    <div v-else
    class="bg-white dark:bg-gray-850 text-gray-800 dark:text-white
                 w-full h-full overflow-y-auto overflow-x-hidden flex flex-col pb-12">
                 <div v-if="errorCodes.length > 0" class="w-full">
        <div class="flex flex-col w-full p-2 pt-4 pb-4 lg:pb-6 dark:border-t -mt-px
                  bg-rose-600/20 dark:bg-rose-800/50 border-rose-700/50 text-center">

            <div v-if="errorCodes.length > 0" class="w-full">
                <div class="bg-rose-900 dark:bg-gray-900 py-2 text-rose-300 dark:text-rose-500 uppercase text-center">
                    Diagnostic Trouble Code{{ errorCodes.length > 1? 'S' : '' }} Detected
                </div>
                <div class="h-4 border-y-4 border-dotted border-error border-rose-500 dark:border-gray-900 mt-3"></div>
                <div class="text-pixel text-4xl text-rose-800 dark:text-rose-300 mt-4">
                    <div v-for="errorCode in errorCodes">
                        <span class="top-1">***</span> {{ errorCode.errorCode }} <span class="top-1">***</span>
                    </div>
                </div>
            </div>

            <!-- ITS AN ISSUE RAISED BY THE HEALTH MONITOR -->
            <div class="h-full w-full flex flex-col justify-center items-center pt-4 pb-4">
                <img src="@/assets/images/health-monitor/carbucks-obd-dark.png" class="w-28 lg:w-32 dark:opacity-60" />
                <div v-if="errorCodes.length > 0" 
                     class="tracking-wider text-sm lg:text-base my-2 text-rose-800 dark:text-rose-200/50">
                    <span v-if="errorCodes.length == 1">This error code</span>
                    <span v-else>These error codes</span>
                    should be reported to your service provider to assist with issue resolution
                </div>
            </div>

        </div>

        <Spacer />

        <div class="p-4 lg:p-6 text-left">
            
            <p class="text-base lg:text-lg">
                The first character in the Diagnostic Trouble Code (DTC) in the panel above, tells you what type of 
                error you're dealing with:
            </p>

            <ul class="mt-4 lg:mt-6 tracking-wider text-sm lg:text-base flex flex-col gap-y-4 lg:gap-y-6">
                <li class="flex items-start gap-4">
                    <div class="flex items-center justify-center rounded bg-gray-100 dark:bg-gray-700 aspect-square h-12 text-lg lg:text-2xl font-bold"
                         :class="{ '!bg-primary-500': anyErrorCodeStartsWith( 'P' )}">
                        P
                    </div>
                    <div>
                        <strong class="text-base lg:text-lg">Powertrain</strong>
                        <div class="opacity-60">Indicates an issue with the engine, transmission or fuel systems and should be treated as a serious safety or warranty issue</div>

                    </div>
                </li>
                <li class="flex items-start gap-4">
                    <div class="flex items-center justify-center rounded bg-gray-100 dark:bg-gray-700 aspect-square h-12 text-lg lg:text-2xl font-bold"
                         :class="{ '!bg-primary-500': anyErrorCodeStartsWith( 'B' )}">
                        B
                    </div>
                    <div>
                        <strong class="text-base lg:text-lg">Body</strong>
                        <div class="opacity-60">Indicates an issue with the body or passenger compartment of the car, typically driving assitance, comfort, or convenience and safety features</div>

                    </div>
                </li>
                <li class="flex items-start gap-4">
                    <div class="flex items-center justify-center rounded bg-gray-100 dark:bg-gray-700 aspect-square h-12 text-lg lg:text-2xl font-bold"
                         :class="{ '!bg-primary-500': anyErrorCodeStartsWith( 'C' )}">
                        C
                    </div>
                    <div>
                        <strong class="text-base lg:text-lg">Chassis</strong>
                        <div class="opacity-60">Indicates an issue with the functions outside of the passenger compartment, such as brakes, steering, suspension and traction control</div>

                    </div>
                </li>
                <li class="flex items-start gap-4">
                    <div class="flex items-center justify-center rounded bg-gray-100 dark:bg-gray-700 aspect-square h-12 text-lg lg:text-2xl font-bold"
                         :class="{ '!bg-primary-500': anyErrorCodeStartsWith( 'U' )}">
                        U
                    </div>
                    <div>
                        <strong class="text-base lg:text-lg">Networking and Electrical</strong>
                        <div class="opacity-60">Indicates an issue with the electrical subsystem, computer software, temperature control, ignition system and power windows and door functions</div>
                    </div>
                </li>
            </ul>

            <p class="text-base lg:text-lg mt-4 lg:mt-6">
                The remaining characters of the code determine the nature of the fault and should be further diagnosed by a trained technician
            </p>

        </div>
    </div>
</div>

</template>
